/**
 * usePerAuthorPostTemplates — real-time read for
 *   projects/{pid}/authors/{authorId}/platforms/{platformId}/post_templates/*
 */

import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { PerAuthorPostTemplate } from '../types';
import type { AuthorBasePath } from '../services/AuthorPlatformService';

export interface UsePerAuthorPostTemplatesReturn {
  templates: PerAuthorPostTemplate[];
  loading: boolean;
  error: Error | null;
}

export function usePerAuthorPostTemplates(
  projectId: string | null | undefined,
  authorId: string | null | undefined,
  platformId: string | null | undefined,
  basePath: AuthorBasePath = 'authors',
): UsePerAuthorPostTemplatesReturn {
  const [templates, setTemplates] = useState<PerAuthorPostTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(
    Boolean(projectId && authorId && platformId),
  );
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !authorId || !platformId) {
      setTemplates([]);
      setLoading(false);
      return;
    }
    const db = CoreFirebaseService.getInstance().firestore;
    const ref = collection(
      db,
      'projects',
      projectId,
      basePath,
      authorId,
      'platforms',
      platformId,
      'post_templates',
    );
    setLoading(true);
    const unsub = onSnapshot(
      query(ref, orderBy('createdAt', 'desc')),
      (snap) => {
        setTemplates(
          snap.docs.map((d) => ({ ...(d.data() as PerAuthorPostTemplate), id: d.id })),
        );
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId, authorId, platformId, basePath]);

  return { templates, loading, error };
}
